import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Form, Input, Checkbox, List, Button, InputNumber, notification, ConfigProvider, Empty, Modal, Space } from 'antd';
import axios from 'axios';
import * as _ from 'lodash';
import { Icon } from 'dlweb-components';

const NewSQLUser = ({hideModal, onSuccess}) => {

  const inputRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [disabled, setDisabled] = useState(true);
  const [starting, setStarting] = useState(false);
  const [userInputValue, setUserInputValue] = useState('');
  const [showPasswordError, setShowPasswordError] = useState(false)

  const [form] = Form.useForm();

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 24 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 24 },
    },
  };

  const checkValues = useCallback(() => {
    const { password, confirmPassword } = form.getFieldsValue();
    const passwordsMatch = password === confirmPassword;

    setDisabled((!password || !passwordsMatch) || users.length === 0);
    setShowPasswordError(password && !passwordsMatch);
  }, [form, users])

  const isDuplicate = () => _.includes(users, userInputValue);

  useEffect(() => checkValues(), [users, checkValues])

  const handleSubmit = values => {

    setStarting(true);
    setDisabled(true);

    const { confirmPassword, ...formValues } = values;

    const postData = {
      ids: users,
      ...formValues
    }

    axios.post('/api/wt/userman/create/multiple', postData).then((res) => {
      if(res.data.errors.length > 0) {
        Modal.error({
          icon: null,
          title: <>
                    <Icon type='times-circle' size="lg" style={{color: 'var(--error-color)', marginRight: '10px'}}/>
                    Error
                 </>,
          content: <div>
                      <ul style={{listStyle: 'none', margin: '20px 0', padding: 0, maxHeight: '200px', overflowY: 'auto'}}>
                        {res.data.errors.map((err, i) => <div key={i}>{err}</div>)}
                      </ul>
                    </div>
        });

        setStarting(false);
        setDisabled(false);
      }
      else {
        setStarting(false);
        setDisabled(false);
        hideModal();
        onSuccess();
        notification.success({ message: `User${users.length > 1 ? 's' : ''} created successfully!` });
      }
    })
    .catch(error => {
      setStarting(false);
      setDisabled(false);
      console.error(error);
      alert('Error creating users!');
    })
  }

  const preventInvalidChars = (e) => {
    // allow alphanumeric, backspace, _, ', .
    if (e.key.match(/[^A-Za-z0-9\x08\_\'\.]/)) {
      e.preventDefault();
    }
  }

  const addUser = (e) => {
    
    e.preventDefault();

    if(!userInputValue || isDuplicate()) { return }

    setUserInputValue('');
    setUsers([...users, userInputValue ]);

    inputRef.current.focus();
  }

  const removeUser = (username) => {
    setUsers(_.filter(users, u => u !== username))
  }

  const sanitizePaste = (e) => {

    e.preventDefault();

    let cleanString = e.clipboardData.getData('text').replace(/[^a-zA-Z0-9\_\'\.]/gi, '').slice(0, 45);

    setUserInputValue(cleanString)
  } 

  return (
    <Container>
      <Form
        form={form}
        {...formItemLayout}
        onFinish={handleSubmit}
        labelAlign="left"
        onValuesChange={checkValues}
        initialValues={{forcePasswordChange: false, passwordMaxDays: 0}}
      >
        <div style={{width: '50%', display: 'inline-block', padding: '10px 15px 10px 10px', verticalAlign: 'top'}}>
        <Form.Item label="Please enter one or more users" onPaste={(e) => sanitizePaste(e)}>
          <Space.Compact block={true}>
            <Input
              autoFocus
              ref={inputRef}
              value={userInputValue}
              onPressEnter={addUser}
              onKeyDown={preventInvalidChars}
              style={{ width: '100%'}}
              onChange={(e) => { setUserInputValue(e.target.value)} }
              maxLength={45}
            />
            <Button type="primary" onClick={addUser} disabled={!userInputValue || isDuplicate()}>Add</Button>
          </Space.Compact>
        </Form.Item>
        <Form.Item label="Add users to Document Locator">
        <ConfigProvider renderEmpty={() => <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Users"/>}>
          <List
            size="small"
            style={{ height: '200px', overflow: 'hidden', overflowY: 'auto' }}
            bordered
            dataSource={users}
            renderItem={username => (
              <List.Item key={username} title={username}>
                <Icon type='user' size="lg" faStyle='fas' style={{color: 'var(--primary-light)'}}/>
                <FileName>{username}</FileName>
                <RemoveButton onClick={() => removeUser(username)}>x</RemoveButton>
              </List.Item>
            )}
          />
        </ConfigProvider>
        </Form.Item>
        </div>
        <div style={{width: '50%', display: 'inline-block', padding: '10px 10px 10px 15px'}}>
        <Form.Item label="Password" name="password">
          <Input.Password visibilityToggle={false}/>
        </Form.Item>
        <Form.Item label="Confirm password" name="confirmPassword" validateStatus={showPasswordError ? 'error' : ''}>
          <Input.Password visibilityToggle={false}
          />
        </Form.Item>
        <Form.Item label="Change password every n days" name="passwordMaxDays">
          <InputNumber min={0} max={999} step={5} />
        </Form.Item>
        <Form.Item style={{ paddingTop: '10px' }} name="forcePasswordChange" valuePropName="checked">
            <Checkbox>New password next login</Checkbox>
        </Form.Item>
        </div>
        <Form.Item
          wrapperCol={{
            xs: { span: 24 },
            sm: { span: 24 },
          }}
          style={{ marginTop: '25px' }}
        >
          <Button
            type="primary"
            htmlType="submit"
            style={{ float: 'right', marginLeft: '10px' }}
            loading={starting}
            disabled={disabled}  
          >
            Create User{users.length > 1 && 's'}
          </Button>
          <Button
            style={{ float: 'right' }}
            onClick={hideModal}
            >
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Container>
  );
};

const FileName = styled.div`
  margin-left: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
  flex: auto;
`

const RemoveButton = styled.div`
  position: absolute;
  right: 0;
  width: 40px;
  height: 38px;
  line-height: 38px;
  text-align: center;
  cursor: pointer;
  background: #f9f9fa;
  user-select: none;
  border-left: 1px solid #efefef; 
  &:hover {
    background: #eceeef;
  }
`

const Container = styled.div`
  .ant-form-item {
    margin-bottom: 0;
  }
`

export default NewSQLUser;